'use client';

import { useEffect, useRef } from 'react';
import { config } from '@/config';
import { useFetchUser } from '@/auth/hooks';
import { useExplorer } from '@/hooks/useExplorer';
import { buildPendoIdentityPayload } from '@/lib/pendo/buildPendoMetadata';

let pendoSdkPromise: Promise<typeof import('@pendo/web-sdk')> | null = null;

function loadPendoSdk() {
  pendoSdkPromise ??= import('@pendo/web-sdk');
  return pendoSdkPromise;
}

async function waitForPendoReady(isCancelled: () => boolean): Promise<boolean> {
  for (let i = 0; i < 50; i++) {
    if (isCancelled()) {
      return false;
    }
    if (typeof window !== 'undefined' && window.pendo?.isReady?.()) {
      return true;
    }
    await new Promise((r) => setTimeout(r, 100));
  }
  return false;
}

/**
 * Loads Pendo via @pendo/web-sdk. Runs in production when `pendoApiKey` is set.
 * Visitor/account come from Beehive session + explorer site selection when logged in;
 * anonymous otherwise. Site switches call `pendo.identify` after initial `initialize`.
 */
export function Pendo() {
  const { user, loading: authLoading = true } = useFetchUser();
  const { siteId, siteName } = useExplorer();

  const initStartedRef = useRef(false);
  const lastSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    if (config.appEnv !== 'production' || !config.pendoApiKey) {
      return;
    }

    if (authLoading) {
      return;
    }

    const payload = buildPendoIdentityPayload(user, siteId, siteName);
    let cancelled = false;
    const isCancelled = () => cancelled;

    void (async () => {
      try {
        const { initialize, Replay } = await loadPendoSdk();
        if (cancelled) {
          return;
        }

        const apiKey = config.pendoApiKey;
        /** Pendo data center; `io` matches default US CDN (same idea as chargebee-ui snippet host). */
        const env = 'io' as const;

        const initOptions = {
          apiKey,
          env,
          visitor: payload.visitor,
          ...(payload.account ? { account: payload.account } : {}),
          plugins: [Replay],
        };

        if (!initStartedRef.current) {
          await initialize(initOptions);
          if (cancelled) {
            return;
          }
          initStartedRef.current = true;
          lastSignatureRef.current = payload.signature;
          return;
        }

        if (lastSignatureRef.current === payload.signature) {
          return;
        }

        const ready = await waitForPendoReady(isCancelled);
        if (!ready || cancelled) {
          return;
        }

        window.pendo?.identify?.({
          visitor: payload.visitor,
          ...(payload.account ? { account: payload.account } : {}),
        });
        lastSignatureRef.current = payload.signature;
      } catch {
        // Init / identify failure is non-fatal
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, user, siteId, siteName]);

  return null;
}
